/**
 * R3F unit layer: every visible unit through the shared `ModelBatcher`,
 * interpolated between ticks about one tick behind the sim (`unitMotion.ts`)
 * and turned to face its next step (rivals fade in and out as they come into
 * and leave sight, keeping their track for a while when hidden), selection rings in one
 * instanced mesh, and badges (activity, load, rank, worker counts, spy
 * status) as instanced quads (`BadgeBatch`). Handles unit clicks with a
 * ray–sphere test against the drawn positions (select own units; report
 * other presses so right-click can target them).
 */
import { type ThreeEvent, useFrame } from '@react-three/fiber';
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { axialToWorld, getHexTileMapKey } from '@/game/hexmap';
import config from '@/game/hexmap/config';
import { type Building, type Cell, fromMilli, type Unit, unitRank, workerSlots } from '@/game/sim';
import { activityLookup, assignedWorkers } from '@/game/view/activity';
import { HEAL_ICON } from '@/game/view/heals';
import { mapLabel } from '@/game/view/names';
import { colorIndexFor, PLAYER_COLORS } from '@/game/view/palette';
import { RESOURCE_ICONS } from '@/game/view/resources';
import { tickEngagements, tickHealings, tickWorkers } from '@/game/view/tickCache';
import { isPinned } from '@/game/view/workerIntent';
import { useFrameStore, useSimFrame } from './app/simFrame';
import { BadgeBatch, type BadgeSpec } from './badgeBatch';
import { insideBadgeQuads, nameLabelQuads, unitBadgeQuads } from './badgeLayout';
import {
  type ClickTiles,
  cursorTileKey,
  isMoving,
  ownClickTiles,
  unitTakesClick,
} from './clickPick';
import { flatRing } from './InstancedMarkers';
import { placeMatrix, unitLook, useModelBatcher } from './ModelBatcher';
import { SPY_MARKERS } from './models';
import { createUnitPicker, type Pickable } from './picking';
import { type UiStore, useUi } from './ui/store';
import { type Motion, motionAt, retarget, sampleMotion, TickClock } from './unitMotion';

const UNIT_Y = config.hexMap.tile.thickness;
const RADIUS = config.hexMap.tile.radius;
/** Height of the floating activity indicator above the unit's feet. */
const ACTIVITY_Y = 1.15;
/** How quickly a unit turns to face where it's heading. */
const TURN_RATE = 10;
/** Over an exposed spy, for everyone. */
const EXPOSED_MARKER = '❗';
/** Selection rings sit just above the unit's tile. */
const RING_Y = 0.03;
/** Batcher entity ids: units' are prefixed so they never meet other layers'. */
const entityId = (id: string) => `u:${id}`;

const selectionRingGeometry = flatRing(0.38, 0.06, 28);
const selectionRingMaterial = new THREE.MeshStandardMaterial({
  color: '#fde047',
  emissive: '#fde047',
  emissiveIntensity: 1.4,
});

interface UnitsLayerProps {
  selectedUnitIds: readonly string[];
  /**
   * Left-click on an own unit; `additive` (shift held) adds/removes it
   * instead of replacing the selection. Omitted while placing, so the click
   * goes through to the tile.
   */
  onSelectUnit?: (id: string, additive: boolean) => void;
  /** Any press on a unit that doesn't select it (right-click, or while placing); nearest first. */
  onUnitPointerDown?: (id: string) => void;
  /** Receives where units are drawn (updated in place each frame), for box-select. */
  drawnRef?: { current: readonly Pickable[] };
  /** The match input store: the player's unit names are read from it. */
  ui: UiStore;
}

/** A drawn unit's interpolated position and heading. */
interface Track {
  x: number;
  z: number;
  yaw: number;
  motion: Motion;
  /** A rival's: it fades in and out of sight instead of popping. */
  rival: boolean;
  /** When it last came into sight (seconds), for its fade-in. */
  shownAt: number;
  /** When a rival went out of sight, while it fades out and its track is kept. */
  hiddenAt: number | null;
  /** Its batcher entity id, made once rather than every frame. */
  key: string;
}

/** Closer than this (radians) a turning unit snaps to its heading. */
const SNAP = 1e-3;
/** A move between ticks longer than this (world units) is a teleport: it snaps. */
const TELEPORT = (() => {
  const step = axialToWorld({ q: 1, r: 0 }, RADIUS);
  return 1.5 * Math.hypot(step.x, step.z);
})();

/** How long a rival takes to fade in or out (seconds). */
const FADE = 0.15;
/** How long a hidden rival's track is kept, so it carries on from there if seen again (seconds). */
const HIDDEN_GRACE = 2;

/** How much of a unit is drawn: 1 in sight, shrinking to 0 as a rival fades out. */
function fadeScale(track: Track, now: number): number {
  if (track.hiddenAt !== null) return Math.max(0, 1 - (now - track.hiddenAt) / FADE);
  return Math.min(1, (now - track.shownAt) / FADE);
}

const fadeVector = new THREE.Vector3();

/** A unit's matrix, scaled down while it fades. */
function trackMatrix(out: THREE.Matrix4, track: Track, now: number): THREE.Matrix4 {
  placeMatrix(out, track.x, UNIT_Y, track.z, track.yaw);
  const f = fadeScale(track, now);
  return f < 1 ? out.scale(fadeVector.setScalar(f)) : out;
}

/** Where a unit is heading this tick: its tile, or part way along its next step. */
function unitTarget(unit: Unit): { x: number; z: number } {
  let q = unit.pos.q;
  let r = unit.pos.r;
  if (unit.path && unit.path.length > 0) {
    const next = unit.path[0];
    const p = fromMilli(unit.progress ?? 0);
    q += (next.q - unit.pos.q) * p;
    r += (next.r - unit.pos.r) * p;
  }
  return axialToWorld({ q, r }, RADIUS);
}

/** Seconds on the clock the unit motion is timed with. */
const nowSeconds = () => performance.now() / 1000;

const scratchMatrix = new THREE.Matrix4();
const ringMatrix = new THREE.Matrix4();

export function UnitsLayer({
  selectedUnitIds,
  onSelectUnit,
  onUnitPointerDown,
  drawnRef,
  ui,
}: UnitsLayerProps) {
  const frames = useFrameStore();
  const {
    view: state,
    tick,
    playerId,
    playerOrder,
    rules,
    terrain,
    ctx: tickCtx,
    fog,
  } = useSimFrame();
  const getTileVisibility = fog.visibility;
  const names = useUi(ui, (s) => s.names);
  const batcher = useModelBatcher();
  const badges = useMemo(() => new BadgeBatch(), []);
  useEffect(() => () => badges.dispose(), [badges]);
  const tracks = useRef(new Map<string, Track>());
  const tickClock = useMemo(() => new TickClock(), []);
  /** The tick the tracks last headed for. */
  const trackedTick = useRef<number | null>(null);
  /** Units this layer has in the batcher. */
  const placed = useRef(new Set<string>());
  /** Hidden rivals still in the batcher, fading out. */
  const fading = useRef(new Set<string>());
  const pickables = useRef<Pickable[]>([]);
  const picker = useMemo(() => createUnitPicker(pickables), []);
  const clickTilesRef = useRef<ClickTiles>({ buildings: new Set(), cities: new Set() });

  const units = Object.values(state.units);
  // Heals show only on a patient in sight (own units always are).
  const activityOf = activityLookup(
    state,
    rules,
    terrain,
    (cell) => getTileVisibility(getHexTileMapKey(cell)) === 'visible',
    tickEngagements(state, tickCtx),
    tickHealings(state, rules)
  );

  // Drawn units: not inside a building (the building's badge, CityPanel and
  // the Units panel account for those); own always, a rival's only while its
  // logical (non-interpolated) tile is visible.
  const shown: Array<{ unit: Unit; color: string }> = [];
  const badgeSpecs: BadgeSpec[] = [];
  for (const unit of units) {
    if (unit.inside) continue;
    if (unit.owner !== playerId && getTileVisibility(getHexTileMapKey(unit.pos)) !== 'visible') {
      continue;
    }
    const color = PLAYER_COLORS[colorIndexFor(unit.owner, playerOrder)] ?? PLAYER_COLORS[0];
    shown.push({ unit, color });
    const activityIcon = activityOf(unit);
    // Spies (the state is the viewer's: viewerState.ts): an exposed one is
    // marked for everyone; the owner sees whether theirs is hidden or
    // blended in. Rivals' hidden or disguised spies never get here as spies.
    const spyMarker = unit.exposed
      ? EXPOSED_MARKER
      : unit.owner === playerId && rules.units[unit.type]?.stealth
        ? unit.disguise
          ? SPY_MARKERS.disguised
          : SPY_MARKERS.stealth
        : null;
    const quads = unitBadgeQuads(
      {
        spyMarker,
        rank: unitRank(rules, unit),
        activityIcon,
        heal: activityIcon === HEAL_ICON,
        carryIcon: unit.carry ? (RESOURCE_ICONS[unit.carry.res] ?? '📦') : null,
        carryAmount: unit.carry ? Math.floor(fromMilli(unit.carry.amount)) : 0,
        // Own units only: a rival's orders aren't ours to know.
        pinned: unit.owner === playerId && isPinned(unit),
      },
      badges.measure
    );
    // Anchored where the unit is drawn; the frame loop keeps it there.
    const track = tracks.current.get(unit.id);
    const at = track ?? unitTarget(unit);
    // The player's own name for it rides in the same badge, over the chips.
    const label = mapLabel(names, unit, playerId);
    if (label) quads.push(...nameLabelQuads(label, badges.measure));
    if (quads.length > 0) {
      badgeSpecs.push({ key: entityId(unit.id), x: at.x, y: UNIT_Y + ACTIVITY_Y, z: at.z, quads });
    }
  }

  // Units inside a building aren't drawn: a farm or forestry (anyone's) and
  // an own city show a count of their workers (barracks: see CityPanel).
  const insideOf = new Map<string, Unit[]>();
  for (const u of units) {
    if (!u.inside) continue;
    const list = insideOf.get(u.inside);
    if (list) list.push(u);
    else insideOf.set(u.inside, [u]);
  }
  const occupied: Array<{ building: Building; workers: Unit[] }> = [];
  for (const [id, workers] of insideOf) {
    const b = state.buildings[id];
    const def = b && rules.buildings[b.type];
    if (!b || !def || !(def.provides || (b.owner === playerId && def.accepts))) continue;
    occupied.push({ building: b, workers });
  }
  // Own farms, forestries and mines count everyone working them, the ones on
  // the road hauling too, so one whose workers are all out still shows them.
  for (const b of Object.values(state.buildings)) {
    if (b.owner !== playerId || b.buildProgress < 1 || !rules.buildings[b.type]?.provides) continue;
    if (!insideOf.has(b.id)) occupied.push({ building: b, workers: [] });
  }
  const seen = (cell: Cell) => getTileVisibility(getHexTileMapKey(cell)) === 'visible';
  for (const { building, workers } of occupied) {
    const own = building.owner === playerId;
    // A rival's: only workers on its tiles in sight, badged on one of them.
    const slots = rules.buildings[building.type]?.provides ? workerSlots(rules, building) : null;
    const count = !own
      ? workers.filter((u) => seen(u.pos)).length
      : slots !== null
        ? assignedWorkers(state, rules, building, tickWorkers(state, rules)).total
        : // An own city: scouts waiting there once the map is explored aren't 👷.
          workers.filter((u) => rules.units[u.type]?.automate !== 'explore').length;
    const at = own || seen(building.pos) ? building.pos : building.footprint?.find(seen);
    if (count === 0 || !at) continue;
    const world = axialToWorld(at, RADIUS);
    // Inside counts first, so units' badges draw over them.
    badgeSpecs.unshift({
      key: `in:${building.id}`,
      x: world.x,
      y: UNIT_Y + ACTIVITY_Y,
      z: world.z,
      quads: insideBadgeQuads(
        own && slots !== null ? `${count}/${slots}` : `${count}`,
        badges.measure,
        own && workers.some(isPinned)
      ),
    });
  }

  // Own building tiles: a left-click whose cursor is over one selects the
  // building unless the unit stands still on that same tile (an own city always
  // wins its own tile); the unit handler lets it through (clickPick.ts).
  const clickTiles = ownClickTiles(state, rules, playerId);
  const selected = new Set(selectedUnitIds);

  // Selection rings: one instanced mesh, grown when the selection outgrows it.
  const ringCapacity = Math.max(32, 2 ** Math.ceil(Math.log2(Math.max(1, selected.size))));
  const rings = useMemo(() => {
    const mesh = new THREE.InstancedMesh(
      selectionRingGeometry,
      selectionRingMaterial,
      ringCapacity
    );
    mesh.count = 0;
    mesh.frustumCulled = false;
    mesh.raycast = () => {};
    return mesh;
  }, [ringCapacity]);
  useEffect(
    () => () => {
      rings.dispose();
    },
    [rings]
  );

  const shownRef = useRef(shown);
  const selectedRef = useRef(selected);
  // Per tick: looks and colours into the batcher (new units snap to their
  // spot), badges laid out, units no longer drawn taken out.
  useLayoutEffect(() => {
    shownRef.current = shown;
    selectedRef.current = selected;
    clickTilesRef.current = clickTiles;
    const keep = new Set<string>();
    // Click targets in `shown` order (the frame loop moves them in place).
    const picks: Pickable[] = [];
    const now = nowSeconds();
    for (const { unit, color } of shown) {
      keep.add(unit.id);
      let track = tracks.current.get(unit.id);
      if (!track) {
        const at = unitTarget(unit);
        const rival = unit.owner !== playerId;
        track = {
          ...at,
          yaw: 0,
          motion: motionAt(at.x, at.z, now),
          rival,
          // Own units are there at once; a rival fades into sight.
          shownAt: rival ? now : Number.NEGATIVE_INFINITY,
          hiddenAt: null,
          key: entityId(unit.id),
        };
        tracks.current.set(unit.id, track);
      } else if (track.hiddenAt !== null) {
        // Back in sight: fade in from however far it had faded out, and head
        // from where it was last drawn (snapping if it went far meanwhile).
        track.shownAt = now - fadeScale(track, now) * FADE;
        track.hiddenAt = null;
        fading.current.delete(unit.id);
        // Usually the frame loop hasn't seen this tick yet and retargets it
        // with the rest; if it already has, head for the tick's spot here.
        if (tick === trackedTick.current) {
          const target = unitTarget(unit);
          retarget(track.motion, target.x, target.z, now, tickClock.due(tick), TELEPORT);
          sampleMotion(track.motion, now, track);
        }
      }
      picks.push({
        id: unit.id,
        x: track.x,
        z: track.z,
        tile: getHexTileMapKey(unit.pos),
        moving: isMoving(unit),
      });
      batcher.set(track.key, unitLook(unit.type), trackMatrix(scratchMatrix, track, now), color);
      placed.current.add(unit.id);
    }
    pickables.current = picks;
    if (drawnRef) drawnRef.current = picks;
    for (const id of placed.current) {
      if (keep.has(id)) continue;
      const track = tracks.current.get(id);
      // A rival out of sight fades out in the frame loop.
      if (track?.rival) {
        if (track.hiddenAt === null) track.hiddenAt = now;
        fading.current.add(id);
        continue;
      }
      batcher.remove(entityId(id));
      placed.current.delete(id);
      tracks.current.delete(id);
    }
    // A rival hidden for long enough snaps to its true spot if seen again.
    for (const [id, track] of tracks.current) {
      if (track.hiddenAt !== null && now - track.hiddenAt > HIDDEN_GRACE && !placed.current.has(id))
        tracks.current.delete(id);
    }
    badges.setBadges(badgeSpecs);
  });
  // Unmount: take every unit out of the shared batcher.
  useEffect(
    () => () => {
      for (const id of placed.current) batcher.remove(entityId(id));
      placed.current.clear();
      fading.current.clear();
    },
    [batcher]
  );

  useFrame(({ size, clock }, delta) => {
    const frame = frames.get();
    const state = frame.view;
    const now = nowSeconds();
    // A new tick (first seen this frame): every track heads for its spot.
    const newTick = frame.tick !== trackedTick.current;
    if (newTick) {
      trackedTick.current = frame.tick;
      tickClock.arrive(frame.tick, now);
    }
    const due = tickClock.due(frame.tick);
    const turn = 1 - Math.exp(-TURN_RATE * delta);
    let ringCount = 0;
    let i = 0;
    for (const { unit: drawn } of shownRef.current) {
      const pick = pickables.current[i++];
      const unit = state.units[drawn.id] ?? drawn;
      const track = tracks.current.get(unit.id);
      if (!track) continue;
      if (newTick) {
        const target = unitTarget(unit);
        retarget(track.motion, target.x, target.z, now, due, TELEPORT);
      }
      sampleMotion(track.motion, now, track);
      // Face the next cell while moving; keep the last heading when idle.
      if (unit.path && unit.path.length > 0) {
        const next = axialToWorld(unit.path[0], RADIUS);
        const from = axialToWorld(unit.pos, RADIUS);
        const heading = Math.atan2(next.x - from.x, next.z - from.z);
        let d = heading - track.yaw;
        d = Math.atan2(Math.sin(d), Math.cos(d));
        track.yaw = Math.abs(d) < SNAP ? heading : track.yaw + d * turn;
      }
      batcher.setMatrix(track.key, trackMatrix(scratchMatrix, track, now));
      badges.setAnchor(track.key, track.x, UNIT_Y + ACTIVITY_Y, track.z);
      if (pick) {
        pick.x = track.x;
        pick.z = track.z;
      }
      if (selectedRef.current.has(unit.id) && ringCount < rings.instanceMatrix.count) {
        rings.setMatrixAt(
          ringCount++,
          ringMatrix.makeTranslation(track.x, UNIT_Y + RING_Y, track.z)
        );
      }
    }
    // Hidden rivals shrink where they were last drawn, then leave the batcher.
    for (const id of fading.current) {
      const track = tracks.current.get(id);
      if (track && fadeScale(track, now) > 0) {
        sampleMotion(track.motion, now, track);
        batcher.setMatrix(track.key, trackMatrix(scratchMatrix, track, now));
        continue;
      }
      batcher.remove(entityId(id));
      placed.current.delete(id);
      fading.current.delete(id);
    }
    rings.count = ringCount;
    if (ringCount > 0) {
      rings.instanceMatrix.clearUpdateRanges();
      rings.instanceMatrix.addUpdateRange(0, ringCount * 16);
      rings.instanceMatrix.needsUpdate = true;
    }
    badges.frame(size.width, size.height, clock.elapsedTime);
  });

  const onPointerDown = (e: ThreeEvent<PointerEvent>) => {
    const hit = e.instanceId === undefined ? undefined : pickables.current[e.instanceId];
    if (!hit) return;
    // Over an own building's tile the building may take the left-click.
    if (
      e.button === 0 &&
      onSelectUnit &&
      !unitTakesClick(hit.tile, cursorTileKey(e.ray), clickTilesRef.current, hit.moving)
    )
      return;
    if (e.button === 0 && onSelectUnit) {
      e.stopPropagation();
      onSelectUnit(hit.id, e.nativeEvent.shiftKey);
    } else {
      onUnitPointerDown?.(hit.id);
    }
  };

  return (
    <>
      <primitive object={picker} onPointerDown={onPointerDown} />
      <primitive object={rings} />
      <primitive object={badges.mesh} />
    </>
  );
}
